import { workOrderDataApi } from "Api/common";
import { getWorkOrderList } from "Api/RepairOrderOpen";
import { addTaxInvoice, editTaxInvoice, viewTaxInvoice, workOrderTaxInvoiceApi } from "Api/taxInvoice";
import BaseButton from "Components/Base/BaseButton";
import BaseInput from "Components/Base/BaseInput";
import Loader from "Components/Base/BaseLoader";
import { BaseSelect } from "Components/Base/BaseSelect";
import { checkStatusCodeSuccess, currentDate, errorHandler, NA, NormalDate, TaxType } from "Components/constants/Common";
import { cancelLabel, submitLabel, updateLabel } from "Components/constants/Role";
import { TaxInvoiceLabel } from "Components/constants/taxInvoice";
import {
  InputPlaceHolder,
  SelectPlaceHolder,
  validationMessages,
} from "Components/constants/validation";
import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import * as Yup from "yup";

const TaxInvoiceForm = () => {
  document.title = TaxInvoiceLabel.TaxInvoiceTitle;
  const navigate = useNavigate();
  const { taxInvoiceId }:any = useParams();
  const [workOrderData, setWorkOrderData] = useState<any>(null);
  const [workOrderOption, setWorkOrderOption] = useState([]);
  const [loader, setLoader] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const [performaId, setProformaId] = useState(null);
  const [viewTaxInvoiceData, setViewTaxInvoiceData] = useState<any>(null);

  const validationSchema = Yup.object({
    workOrderId: Yup.number().required(validationMessages.required(TaxInvoiceLabel.workOrderNo)),
    taxInvoiceDT: Yup.date().required(validationMessages.required(TaxInvoiceLabel?.TaxInvoiceDT)),
    remarks: Yup.string().required(validationMessages.required(TaxInvoiceLabel?.Remarks)),
  });

  const fetchWorkOrderNo = () => {
    setLoading(true);
    const apiCall = taxInvoiceId ? getWorkOrderList : workOrderTaxInvoiceApi;
    apiCall()
      .then((res) => {
        if (checkStatusCodeSuccess(res?.statusCode)) {
          setWorkOrderOption(
            res?.data?.map((item: any) => ({
              value: item?.id,
              label: item?.work_order_number,
              id: item?.id,
            }))
          );
        } else {
          setWorkOrderOption([]);
        }
      })
      .catch((err) => {
        setWorkOrderOption([]);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const fetchTaxInvoiceData = () => {
    setLoading(true);
    viewTaxInvoice(Number(taxInvoiceId))
      .then((res) => {
        if (checkStatusCodeSuccess(res?.statusCode)) {
          setViewTaxInvoiceData(res?.data);
        } else {
          setViewTaxInvoiceData(null);
        }
      })
      .catch((err) => {
        setViewTaxInvoiceData(null);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const fetchWorkOrderData = (id: number) => {
    setLoading(true);
    workOrderDataApi(id)
      .then((res) => {
        if (checkStatusCodeSuccess(res?.statusCode)) {
          const data = res?.data;
          setWorkOrderData({
            vendorName: data?.customer_name ?? "",
            vendorId: data?.customer_id ?? "",
            taxableValue: data?.work_order_exl_tax_amount ?? '',
            gst: data?.gst_amount ?? '',
            taxInvoiceAmt: data?.work_order_incl_tax_amount ?? '',
            warranty: data?.warranty ?? '',
            proformaInvoiceNo: data?.proformaInvoice?.pro_in_no ?? NA,
            proformaInvoiceDT: data?.proformaInvoice?.pro_in_dt ?? "",
          });
          setProformaId(data?.proforma_id)
        } else {
          setWorkOrderData({});
        }
      })
      .catch((err) => {
        setWorkOrderData({});
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchWorkOrderNo();
    if (taxInvoiceId) {
      fetchTaxInvoiceData();
    }
  }, []);



  const formInitialValue = {
    workOrderId: viewTaxInvoiceData ? viewTaxInvoiceData?.work_order_id : null,
    vendorName: '',
    vendorId: viewTaxInvoiceData ? viewTaxInvoiceData?.customer_id : '',
    taxInvoiceDT: viewTaxInvoiceData ? NormalDate(viewTaxInvoiceData?.tax_in_dt) : currentDate,
    taxableValue: viewTaxInvoiceData ? viewTaxInvoiceData?.workOrder?.work_order_exl_tax_amount : '',
    gst: viewTaxInvoiceData ? viewTaxInvoiceData?.workOrder?.gst_amount : '',
    taxInvoiceAmt: viewTaxInvoiceData ? viewTaxInvoiceData?.workOrder?.work_order_incl_tax_amount : '',
    warranty: viewTaxInvoiceData ? viewTaxInvoiceData?.workOrder?.warranty : '',
    proformaInvoiceDT: viewTaxInvoiceData ? viewTaxInvoiceData?.pro_in_dt : '',
    remarks: viewTaxInvoiceData ? viewTaxInvoiceData?.remarks : null,
    proformaInvoiceNo: viewTaxInvoiceData ? viewTaxInvoiceData?.pro_in_no : ''
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: formInitialValue,
    validationSchema,
    onSubmit: (values) => {
      handleFormSubmit(values);
    },
  });

  useEffect(() => {
    formik.values.workOrderId && fetchWorkOrderData(Number(formik.values.workOrderId))
  }, [formik.values.workOrderId])

  useEffect(() => {
    if (workOrderData) {
      formik.setValues((prevValues: any) => ({
        ...prevValues,
        ...workOrderData
      }))
    } else {
      formik.setValues((prevValues: any) => ({
        ...prevValues,
        vendorName: '',
        vendorId: '',
        taxableValue: '',
        gst: '',
        taxInvoiceAmt: '',
        warranty: '',
        proformaInvoiceNo: '',
        proformaInvoiceDT: ''
      }))
    }
  }, [workOrderData])

  const handleFormSubmit = (values: any) => {
    setLoader(true);
    const payload = {
      proforma_id: performaId,
      work_order_id: values.workOrderId,
      customer_name: values.vendorName,
      customer_id: values.vendorId,
      tax_in_dt: values.taxInvoiceDT,
      taxable: values.taxableValue,
      gst: values.gst,
      tax_invoice_amount: values.taxInvoiceAmt,
      warranty: values.warranty,
      remarks: values.remarks,
      ...(values.proformaInvoiceNo !== NA && { pro_in_no: values.proformaInvoiceNo }),
      pro_in_dt: values.proformaInvoiceDT,
    };
    if (taxInvoiceId) {
      editTaxInvoice(Number(taxInvoiceId), payload)
        .then((res) => {
          if (checkStatusCodeSuccess(res?.statusCode)) {
            toast.success(res?.message);
            navigate("/tax-invoice");
          } else {
            toast.error(res?.message);
          }
        })
        .catch((err) => {
          errorHandler(err);
        })
        .finally(() => {
          setLoader(false);
        });
    } else {
      addTaxInvoice(payload)
        .then((res) => {
          if (checkStatusCodeSuccess(res?.statusCode)) {
            toast.success(res?.message);
            navigate("/tax-invoice");
          } else {
            toast.error(res?.message);
          }
        })
        .catch((err) => {
          errorHandler(err);
        })
        .finally(() => {
          setLoader(false);
        });
    }
  };

  return (
    <>
      <div className="page-content">
        {loading && <Loader />}
        <div className="flex-grow-1 mx-3">
          <div className="mb-1 mx-2 d-flex justify-content-between align-items-center">
            <span className="fs-4">
              {taxInvoiceId
                ? `${TaxInvoiceLabel.Update} ${TaxInvoiceLabel?.TaxInvoice}-${viewTaxInvoiceData?.tax_in_no || ""}`
                : `${TaxInvoiceLabel.Add} ${TaxInvoiceLabel?.TaxInvoice}`
              }
            </span>
            <button
              className="btn btn-danger"
              onClick={() => navigate("/tax-invoice")}
            >
              <i className="mdi mdi-arrow-left" />
            </button>
          </div>
          <div className="card word-wrap">
            <div className="card-body">
              <form>
                <div className="row mt-2">
                  <div className="col-md-3 col-sm-6 col-12">
                    <BaseSelect
                      name="workOrderId"
                      label={TaxInvoiceLabel.workOrderNo}
                      options={workOrderOption}
                      placeholder={SelectPlaceHolder(TaxInvoiceLabel.workOrderNo)}
                      value={formik.values.workOrderId}
                      handleChange={(field: string, value: any) => {
                        formik.setFieldValue(field, value);
                      }}
                      handleBlur={formik.handleBlur}
                      error={formik.errors.workOrderId}
                      touched={formik.touched.workOrderId}
                      required
                    />
                  </div>
                  <div className="col-md-3 col-sm-6 col-12">
                    <BaseInput name="vendorName"
                      label={TaxInvoiceLabel.VendorName}
                      value={formik.values.vendorName}
                      placeholder={SelectPlaceHolder(TaxInvoiceLabel.VendorName)}
                      disabled
                    />
                  </div>
                  <div className="col-md-3 col-sm-6 col-12">
                    <BaseInput
                      name="vendorId"
                      label={TaxInvoiceLabel.VendorId}
                      value={formik.values.vendorId}
                      placeholder={SelectPlaceHolder(TaxInvoiceLabel.VendorId)}
                      disabled
                    />
                  </div>
                  <div className="col-md-3 col-sm-6 col-12">
                    <BaseInput
                      name="taxInvoiceDT"
                      label={TaxInvoiceLabel.TaxInvoiceDT}
                      value={formik.values.taxInvoiceDT}
                      placeholder={InputPlaceHolder(TaxInvoiceLabel.TaxInvoiceDT)}
                      onChange={formik.handleChange}
                      handleBlur={formik.handleBlur}
                      error={formik.errors.taxInvoiceDT}
                      touched={formik.touched.taxInvoiceDT}
                      type="date"
                      required
                    />
                  </div>
                </div>
                <div className="row mt-2">
                  <div className="col-md-3 col-sm-6 col-12">
                    <BaseInput name="taxableValue"
                      label={TaxInvoiceLabel.TaxableValue}
                      value={formik.values.taxableValue}
                      placeholder={InputPlaceHolder(TaxInvoiceLabel.TaxableValue)}
                      disabled
                    />
                  </div>
                  <div className="col-md-3 col-sm-6 col-12">
                    <BaseInput name="gst"
                      label={TaxInvoiceLabel.GST}
                      value={formik.values.gst}
                      disabled
                      placeholder={InputPlaceHolder(TaxInvoiceLabel.GST)}
                    />
                  </div>
                  <div className="col-md-3 col-sm-6 col-12">
                    <BaseInput
                      name="taxInvoiceAmt"
                      label={`${TaxInvoiceLabel.TaxInvoiceAmt} ${TaxInvoiceLabel.includeTax}`}
                      value={formik.values.taxInvoiceAmt}
                      disabled
                      placeholder={TaxInvoiceLabel.TaxInvoiceAmt}
                    />
                  </div>
                  <div className="col-md-3 col-sm-6 col-12">
                    <BaseInput name="warranty"
                      label={`${TaxInvoiceLabel.Warranty} ${TaxInvoiceLabel?.valueFromWorkOrder}`}
                      value={formik.values.warranty}
                      disabled placeholder={InputPlaceHolder(TaxInvoiceLabel.Warranty)}
                    />
                  </div>
                </div>
                <div className="row mt-2">
                  <div className="col-md-3 col-sm-6 col-12">
                    <BaseInput
                      type="textarea"
                      name="remarks"
                      label={TaxInvoiceLabel.Remarks}
                      value={formik.values.remarks}
                      onChange={formik.handleChange}
                      placeholder={InputPlaceHolder(TaxInvoiceLabel.Remarks)}
                      handleBlur={formik.handleBlur}
                      error={formik.errors.remarks}
                      touched={formik.touched.remarks}
                      required
                      rows={1}
                    />
                  </div>
                  <div className="col-md-3 col-sm-6 col-12">
                    <BaseInput
                      name="taxInvoice"
                      label={TaxInvoiceLabel.ProformaInvoiceNo}
                      value={formik.values.proformaInvoiceNo}
                      placeholder={InputPlaceHolder(TaxInvoiceLabel.ProformaInvoiceNo)}
                      onChange={formik.handleChange}
                      disabled
                    />
                  </div>
                  <div className="col-md-3 col-sm-6 col-12">
                    <BaseInput name="proformaInvoiceDT"
                      label={TaxInvoiceLabel.ProformaInvoiceDT}
                      value={formik.values.proformaInvoiceDT}
                      disabled
                      placeholder={InputPlaceHolder(TaxInvoiceLabel.ProformaInvoiceDT)}
                      type="date"
                    />
                  </div>
                </div>
                <div className="row mt-3 d-flex justify-content-end">
                  <div className="col-auto d-flex justify-content-end gap-2 text-end">
                    <BaseButton
                      type="button"
                      label={taxInvoiceId ? updateLabel : submitLabel}
                      color="success"
                      loader={loader}
                      disable={loader}
                      onClick={() => formik.handleSubmit()}
                    />
                    <BaseButton
                      type="button"
                      label={cancelLabel}
                      onClick={() => navigate("/tax-invoice")}
                      color="danger"
                    />
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TaxInvoiceForm;
